/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react/macro'
import { CloseOutlined, ArrowRightOutlined } from '@ant-design/icons'
import { App, Modal, Alert, Checkbox } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useModalScroll } from 'src/hooks/useModalScroll'
import Text from 'src/components/ts-components/Text'
import useWindowSize from 'src/hooks/useWindowSize'
import Button from 'src/components/ts-components/Button'
import req from '@sportninja/common/api/request'
import {
  getErrorMessage,
  getImageThumbnailId,
} from '@sportninja/common/utils/utils'
import { MappedSport } from '@sportninja/common/hooks/useNewSports'
import { ROUTES } from '@sportninja/common/constants/app'

const styles = css`
  .my-modal-body {
  }
  .my-modal-mask {
    backdrop-filter: blur(1px);
  }
  .my-modal-header {
    border-bottom: 1px dotted ${colors.FIERY_RED};
  }
  .my-modal-footer {
    color: ${colors.FIERY_RED};
  }
  .my-modal-content {
    border: 1px solid #333;
  }
`

const classNames = {
  body: styles['my-modal-body'],
  mask: styles['my-modal-mask'],
  header: styles['my-modal-header'],
  footer: styles['my-modal-footer'],
  content: styles['my-modal-content'],
}

const modalStyles = {
  header: {
    backgroundColor: colors.SECONDARY_300,
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  body: {
    borderBottom: `1px solid ${colors.SOFT_STEEL}`,
    borderTop: `1px solid ${colors.SOFT_STEEL}`,
    backgroundColor: colors.SECONDARY_300,
  },
  content: {
    backgroundColor: colors.SECONDARY_300,
    paddingTop: '8px',
    paddingBottom: '16px',
    paddingLeft: '0',
    paddingRight: '0',
  },
  wrapper: {
    zIndex: 10002,
  },
  mask: {
    backdropFilter: 'blur(2px)',
    backgroundColor: 'rgba(6, 10, 16, 0.8)',
    zIndex: 10001,
  },
  footer: {
    paddingLeft: '16px',
    paddingRight: '16px',
  },
}

interface Team {
  id: string
  name: string
  logoUrl: string
  playerCount: number
  affiliateCount: number
  snTeamId: string
  rosterId: string
  daySmartId?: string
  salesforceId?: string
  organization: string
  parentOrg: string
  sport: string
  teamOfficials: Array<{
    name: string
    email: string
  }>
  previousCompetitions: Array<{
    name: string
    rosterId: string
    logoUrl: string
  }>
}

interface ReviewMergeModalProps {
  isOpen: boolean
  onClose: () => void
  teams: Team[]
  selectedTeamId?: string
  onMergeComplete: () => void
  selectedProperties: {
    [key: string]: {
      teamId: string
      value: string
    }
  }
  findSport: (sportId: string, genericSportId: string) => MappedSport | null
}

const ReviewMergeModal = ({
  isOpen,
  onClose,
  teams,
  selectedTeamId,
  onMergeComplete,
  selectedProperties,
  findSport,
}: ReviewMergeModalProps) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width <= 768, [width])
  const { message } = App.useApp()
  const { openModal, closeModal } = useModalScroll()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isDryRun, setIsDryRun] = useState(false)

  const selectedTeam = useMemo(() => {
    return teams.find((team) => team.id === selectedTeamId)
  }, [teams, selectedTeamId])

  const teamsToMerge = useMemo(() => {
    return teams.filter((team) => team.id !== selectedTeamId)
  }, [teams, selectedTeamId])

  useEffect(() => {
    if (isOpen) {
      openModal()
    } else {
      closeModal()
    }
  }, [closeModal, isOpen, openModal])

  const handleMerge = useCallback(async () => {
    if (!selectedTeamId || teamsToMerge.length === 0) {
      message.error(
        'Please select a team to merge from and a destination team.'
      )
      return
    }

    try {
      setIsLoading(true)
      setError(null)

      const teams_merge_from = [
        ...teamsToMerge,
        ...(selectedTeam ? [selectedTeam] : []),
      ].reduce((collector, team) => {
        const teamProperties = Object.entries(selectedProperties)
          .filter(([_, value]) => value.teamId === team.id)
          .map(([key, value]) => {
            // Handle sport property specially since it contains both sport_id and generic_sport_id
            if (key === 'sport') {
              const sportData = JSON.parse(value.value)
              return [
                { key: 'sport_id', value: sportData.sport_id },
                {
                  key: 'generic_sport_id',
                  value: sportData.generic_sport_id,
                },
              ]
            }
            if (key === 'daysmart_id') {
              return [{ key: 'daysmart_import_id', value: value.value }]
            }
            return [
              {
                key: key.toLowerCase(),
                value: value.value,
              },
            ]
          })
          .flat()
        return {
          ...collector,
          [team.id]: {
            properties: teamProperties.map((prop) => prop.key),
            values: Object.fromEntries(
              teamProperties.map((prop) => [prop.key, prop.value])
            ),
          },
        }
      }, {})

      await req(`/teams/${selectedTeamId}/merge`, {
        method: 'PUT',
        body: JSON.stringify({ teams_merge_from: teams_merge_from }),
        query: { debug: isDryRun ? 1 : 0 },
      })

      message.success({
        content: (
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
            `}
          >
            <span>
              {isDryRun
                ? 'Dry Run Completed Successfully. No Changes Were Made.'
                : 'Teams Merged Successfully.'}
            </span>
            {!isDryRun && (
              <a
                href={`${ROUTES.TEAM_ROOT}/${selectedTeamId}`}
                css={css`
                  color: ${colors.SUCCESS_300};
                  text-decoration: underline;
                  &:hover {
                    color: ${colors.PRIMARY};
                  }
                `}
              >
                View Team
              </a>
            )}
          </div>
        ),
        duration: 10,
        // @ts-ignore
        closeIcon: true,
      })

      onMergeComplete()
      onClose()
    } catch (e) {
      const errorMessage = getErrorMessage(e)
      setError(errorMessage || 'Failed To Merge Teams')
    } finally {
      setIsLoading(false)
    }
  }, [
    selectedTeamId,
    teamsToMerge,
    isDryRun,
    message,
    onMergeComplete,
    onClose,
    selectedProperties,
    selectedTeam,
  ])

  const getFooter = useCallback(() => {
    return (
      <div
        css={css`
          display: flex;
          justify-content: flex-end;
          gap: 16px;
          width: ${isMobile ? '100%' : '50%'};
          margin-left: ${isMobile ? '0' : 'auto'};
          @media (max-width: 768px) {
            width: 100%;
            flex-direction: column;
          }
        `}
      >
        <Button
          text='Cancel'
          onClick={() => {
            setError(null)
            onClose()
          }}
          variant='secondary'
          disabled={isLoading}
        />
        <Button
          text={isDryRun ? 'Run Dry Run' : 'Merge'}
          onClick={handleMerge}
          variant='primary'
          disabled={isLoading}
          isLoading={isLoading}
        />
      </div>
    )
  }, [handleMerge, isMobile, isLoading, onClose, isDryRun])

  const getHeader = useCallback(() => {
    return (
      <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
        Review & Merge Teams
      </Text>
    )
  }, [])

  const closeIcon = useCallback(() => {
    return (
      <CloseOutlined
        css={css`
          color: ${isLoading ? colors.NEUTRAL_50 : colors.SOLITUDE};
          font-size: 18px;
          cursor: ${isLoading ? 'not-allowed' : 'pointer'};
          transition: all 0.2s;
          opacity: ${isLoading ? 0.5 : 1};
          &:hover {
            color: ${isLoading ? colors.NEUTRAL_50 : colors.WHITE};
          }
        `}
      />
    )
  }, [isLoading])

  const renderTeamItem = useCallback(
    (team: Team) => {
      return (
        <div
          key={team.id}
          css={css`
            display: flex;
            flex-direction: column;
            gap: 8px;
            width: 100%;
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
            `}
          >
            <div
              css={css`
                width: 24px;
                height: 24px;
                position: relative;
                background: white;
                overflow: hidden;
                border-radius: 4px;
              `}
            >
              <img
                src={
                  getImageThumbnailId(team) ||
                  '/images/generic_placeholders/placeholder_team.png'
                }
                alt={`${team.name} logo`}
                css={css`
                  width: 24px;
                  height: 24px;
                  position: absolute;
                  left: 0;
                  top: 0;
                  object-fit: cover;
                `}
              />
            </div>
            <Text
              variant='paragraphSmall'
              weight='regular'
              color={colors.WHITE}
            >
              {team.name}
            </Text>
          </div>
          {Object.entries(selectedProperties)
            .filter(([_, value]) => value.teamId === team.id)
            .map(([key, value]) => (
              <div
                key={key}
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 8px;
                  padding-left: 32px;
                `}
              >
                <Text
                  variant='paragraphXSmall'
                  weight='regular'
                  color={colors.NEUTRAL_50}
                  style='text-transform: capitalize;'
                >
                  {key.replace(/_/g, ' ')}:
                </Text>
                <Text
                  variant='paragraphXSmall'
                  weight='medium'
                  color={colors.NEUTRAL_0}
                >
                  {key === 'sport'
                    ? findSport(
                        JSON.parse(value.value).sport_id,
                        JSON.parse(value.value).generic_sport_id
                      )?.name_full || value.value
                    : value.value}
                </Text>
              </div>
            ))}
        </div>
      )
    },
    [selectedProperties, findSport]
  )

  return (
    <Modal
      css={css`
        display: flex;
        flex: 1;
        height: ${isMobile ? '100vh' : 'auto'} !important;
        min-height: 400px;
        margin: ${isMobile ? '0' : '24px'};
        padding: 0;
        max-width: ${isMobile ? '100%' : '80vw'};
        top: ${isMobile ? '0' : '50%'};
        .ant-modal-content {
          height: ${isMobile ? '100vh' : 'auto'} !important;
          min-height: 400px;
          border-radius: ${isMobile ? '0' : '8px'};
          display: flex;
          flex-direction: column;
        }
        .ant-modal-body {
          flex: 1;
          overflow: auto;
          display: flex;
          flex-direction: column;
          padding: 16px;
          background: ${colors.SECONDARY_300};
        }
      `}
      width={isMobile ? '100%' : '40vw'}
      closeIcon={closeIcon()}
      title={getHeader()}
      footer={getFooter()}
      open={isOpen}
      onCancel={
        isLoading
          ? undefined
          : () => {
              setError(null)
              onClose()
            }
      }
      centered={!isMobile}
      maskClosable={!isLoading}
      classNames={classNames}
      styles={modalStyles}
    >
      <div
        css={css`
          align-self: stretch;
          flex-direction: column;
          justify-content: flex-start;
          align-items: flex-start;
          gap: 16px;
          display: flex;
          opacity: ${isLoading ? 0.6 : 1};
          transition: opacity 0.3s ease;s
        `}
      >
        <Text variant='paragraphMedium' weight='regular' color={colors.WHITE}>
          Review The Teams Below. When Ready, Click Merge To Confirm.
        </Text>

        <div
          css={css`
            align-self: stretch;
            flex-direction: column;
            justify-content: center;
            align-items: flex-end;
            gap: 16px;
            display: flex;
            flex: 1;
            height: 100%;
          `}
        >
          <div
            css={css`
              align-self: stretch;
              justify-content: flex-start;
              align-items: center;
              gap: 16px;
              display: flex;
            `}
          >
            <div
              css={css`
                flex: 1;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                gap: 8px;
                display: flex;
              `}
            >
              <Text
                variant='captionLarge'
                weight='semiBold'
                color={colors.WHITE}
                style='text-transform: uppercase;'
              >
                Merging Teams
              </Text>
              <div
                css={css`
                  align-self: stretch;
                  padding: 8px;
                  /* background: ${colors.SECONDARY_200}; */
                  border-radius: 8px;
                  border: 1px solid ${colors.SECONDARY_100};
                  flex-direction: column;
                  justify-content: flex-start;
                  align-items: flex-start;
                  gap: 8px;
                  display: flex;
                  min-height: 100px;
                `}
              >
                {teamsToMerge.map((team) => renderTeamItem(team))}
              </div>
            </div>

            <ArrowRightOutlined
              css={css`
                color: ${colors.SECONDARY_100};
                font-size: 24px;
                margin-top: 16px;
              `}
            />

            <div
              css={css`
                flex: 1;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                gap: 8px;
                display: flex;
              `}
            >
              <Text
                variant='captionLarge'
                weight='semiBold'
                color={colors.WHITE}
                style='text-transform: uppercase;'
              >
                Destination Team
              </Text>
              <div
                css={css`
                  align-self: stretch;
                  padding: 8px;
                  /* background: ${colors.SECONDARY_200}; */
                  border-radius: 8px;
                  border: 1px solid ${colors.SECONDARY_100};
                  flex-direction: column;
                  justify-content: flex-start;
                  align-items: flex-start;
                  gap: 8px;
                  display: flex;
                  min-height: 100px;
                `}
              >
                {selectedTeam && renderTeamItem(selectedTeam)}
              </div>
            </div>
          </div>

          <div
            css={css`
              align-self: stretch;
              justify-content: flex-start;
              align-items: flex-start;
              gap: 8px;
              /* display: flex; */
              display: none;
            `}
          >
            <Checkbox
              checked={isDryRun}
              onChange={(e) => setIsDryRun(e.target.checked)}
              css={css`
                .ant-checkbox-inner {
                  background-color: ${isDryRun
                    ? colors.WARNING_500
                    : colors.SECONDARY_200};
                  border-color: ${isDryRun
                    ? colors.WARNING_500
                    : colors.NEUTRAL_50};
                }
                .ant-checkbox-wrapper:hover .ant-checkbox-inner {
                  border-color: ${colors.WARNING_500};
                }
              `}
            />
            <div
              css={css`
                flex: 1;
                flex-direction: column;
                justify-content: flex-start;
                align-items: flex-start;
                display: flex;
              `}
            >
              <Text
                variant='paragraphSmall'
                weight='medium'
                color={colors.NEUTRAL_0}
              >
                Dry Run?
              </Text>
              <Text
                variant='paragraphXSmall'
                weight='regular'
                color={colors.NEUTRAL_50}
              >
                Run The Merge Without Applying Changes. Preview How The Merged
                Data Would Look.
              </Text>
            </div>
          </div>
          {error && (
            <Alert
              message={error}
              type='error'
              showIcon
              css={css`
                margin-bottom: 16px;
                width: 100%;
              `}
            />
          )}
        </div>
      </div>
    </Modal>
  )
}

export default ReviewMergeModal
